import {format, addDays, subDays, subWeeks, subMonths, parseISO} from 'date-fns'

// Check if this is a UTC date. If so, we should add offset of the local timezone, or the dates could be off by one day.
// e.g. "2024-01-21T00:00:00.000Z" will render as 2024-01-20 in EST, since UTC is ahead of EST
//
// Use the offset in effect *on that date*, not today's. Reading today's offset makes
// every value on the other side of a DST boundary land a day early: viewed in July
// (EDT, -4), "2026-01-15" is UTC midnight = Jan 14 19:00 EST, and adding 4 instead of
// 5 leaves it on Jan 14. Bare YYYY-MM-DD values always hit this branch, so a billing
// history spanning a DST change renders half its dates off by one.
// Turn whatever a form hands us into a Date. date-fns 3+ requires a string for
// parseISO and throws on anything else, but a date that has been through DateModal
// arrives as a Date object, and some callers pass an epoch number. Answers undefined
// for a date that simply isn't set, so callers can guard on the result. An
// unparseable string still yields an Invalid Date, which the date-fns predicates
// handle without throwing.
export const parseDate = (value) => {
    if (!value) return undefined;
    return typeof value === 'string' ? parseISO(value) : new Date(value);
};

/*
    True for a value that names a calendar date rather than an instant: a bare YYYY-MM-DD,
    or anything else landing exactly on UTC midnight. A due date is a day the merchant
    chose, not a moment, so it has to read as that same day wherever it is rendered.
*/
export const isCalendarDate = (dt) => (
    dt instanceof Date &&
    dt.getUTCHours() === 0 &&
    dt.getUTCMinutes() === 0 &&
    dt.getUTCSeconds() === 0 &&
    dt.getUTCMilliseconds() === 0
);

export const adjustTimeByTimeZoneIfUTC = (dt) => {
    if (!dt || !(dt instanceof Date)) return;
    /*
        The guard used to read getMinutes/getSeconds, which are local. In a zone whose
        offset is not a whole number of hours it therefore never matched - at -3:30 the
        local minutes of UTC midnight are 30 - and the value fell through unshifted to
        render a day early. The shift is in whole minutes for the same reason: dividing
        the offset by 60 gave 3.5, and setHours truncates that to 3, leaving the date
        exactly where it started.
    */
    if (isCalendarDate(dt)) {
        dt.setMinutes(dt.getMinutes() + dt.getTimezoneOffset());
    }
}

/*
    One shape for every date we print, so formatDate and its UTC-pinned twin below cannot
    drift apart and reserve mismatched widths.
*/
const dateFormatOptions = (shortDate, ignoreSameYear, year, zone) => ({
    ...zone,
    day: '2-digit',
    month: shortDate ? '2-digit' : 'short',
    ...(ignoreSameYear && (year === getCurrentYear()) ? {} : { year: 'numeric' })
});

export const formatDate = (date, shortDate=false, ignoreSameYear=false) => {
    if (!date) return '';
    const dt = new Date(date);
    /*
        A calendar date renders in UTC, the zone it was written in, so the day cannot drift
        with the reader's location and the same value is safe to put in server HTML. Moving
        the instant into local time instead - what this used to do - has offset arithmetic
        to get wrong, and got it wrong for every zone offset by a fraction of an hour.
        Instants carry a real time of day and keep rendering in the reader's own zone.
    */
    const calendarDate = isCalendarDate(dt);

    return dt.toLocaleDateString('en-US', dateFormatOptions(
        shortDate,
        ignoreSameYear,
        calendarDate ? dt.getUTCFullYear() : dt.getFullYear(),
        calendarDate ? { timeZone: 'UTC' } : {}
    ));
}

/*
    formatDate's output pinned to UTC whatever the value is, instants included.

    Server HTML and the browser's first render have to come out identical, so anything
    drawn before hydration has to name its zone rather than read the machine's. The format
    is fixed width - "Sep 15, 2026" whichever day it names - so this also reserves exactly
    the room the real, local value will take.
*/
export const formatDateInUTC = (date, shortDate=false, ignoreSameYear=false) => {
    if (!date) return '';
    const dt = new Date(date);
    return dt.toLocaleDateString('en-US', dateFormatOptions(
        shortDate, ignoreSameYear, dt.getUTCFullYear(), { timeZone: 'UTC' }
    ));
}

export const getDateInLocalTime = (dateStr) => {
    const dt = dateStr ? new Date(dateStr) : new Date();
    adjustTimeByTimeZoneIfUTC(dt);
    return dt;
}

export const formatMonth = (date, shortDate=false) => {
    return date && new Date(date)
        .toLocaleDateString('en-US', shortDate ? {
            month: "2-digit",
            year: "numeric"
        } : {
            month: 'short',
            year: 'numeric',
        });
}

export const formatDateMonth = (date) => {
    if (!date) return '';
    return getDateInLocalTime(date)
        .toLocaleDateString('en-US', {
            day: '2-digit',
            month: 'short',
        });
}

export const formatDateUTC = (date) => {
    return date && getDateInLocalTime(date)
        .toLocaleDateString('en-US', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            timeZone: 'UTC'
        });
}

// Parse MM-DD-YY (output of formatNumericDate with '-' sep) back to YYYY-MM-DD
// so Grommet DateInput can consume it. Pass-through for ISO or empty values.
export const parseNumericDate = (value) => {
    if (!value || typeof value !== 'string') return value || '';
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value;
    const match = value.match(/^(\d{2})-(\d{2})-(\d{2})$/);
    if (!match) return '';
    const [, mm, dd, yy] = match;
    return `20${yy}-${mm}-${dd}`;
};

// DD/MM/YY
export const formatNumericDate = (date, sep) => {
    if (!date) return '';

    let dt;
    if (typeof date === 'string') {
        // Handle ISO date strings (YYYY-MM-DD) by treating them as local dates
        // This prevents off-by-one errors due to timezone conversion
        if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
            const [year, month, day] = date.split('-').map(Number);
            dt = new Date(year, month - 1, day); // month is 0-indexed
        } else {
            dt = new Date(date);
        }
    } else {
        dt = new Date(date);
    }

    if (isNaN(dt.getTime())) return '';

    const formattedDate = dt.toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
    });
    return sep && formattedDate ? formattedDate.replace(/\//g, sep) : formattedDate;
}

export const formatddMMyyDate = (date) => format(date, 'ddMMMyy');

export const formatTime = (date) => date && new Date(date).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
});

export const formatLastSynced = (date) => {
    if (!date) return null;
    const isCurrentYear = new Date(date).getFullYear() === new Date().getFullYear();
    return isCurrentYear
        ? `Last synced ${formatDate(date, false, true)} at ${formatTime(date)}`
        : `Last synced ${formatDate(date)}`;
};

export const daysFromDate = (date, days) => {
    const parsedDate = new Date(date);
    const futureDate = parsedDate.setDate(parsedDate.getDate() + days);
    return date && new Date(futureDate).toLocaleDateString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
};

export const formatYear = (date) => date && new Date(date)
    .toLocaleDateString('en-US', {
        year: 'numeric',
    });

export const formatMonthYear = (date) => date && new Date(date)
    .toLocaleDateString('en-US', {
        month: '2-digit',
        year: 'numeric',
    });

export const isDateInRange = (date, range) => {
    if (!range || range.length !== 2) return false;
    return ((date >= range[0]) && (date <= range[1]));
}

export const getTodaysDate = () => {
    const now = new Date();
    return format(now, 'ddMMMyy');
}

export const getDateFromPast = (number, unit) => {
    const today = new Date();

    switch (unit) {
    case 'days':
        return subDays(today, number);
    case 'weeks':
        return subWeeks(today, number);
    case 'months':
        return subMonths(today, number);
    default:
        throw new Error('Invalid unit. Use "days", "weeks", or "months".');
    }
};

export const dateToEpochSecond = (date) => date.getTime() / 1000;

export const getDiffDate = (date1, date2, isAbs=true, endOfDay=false) => {
    let dateA = date1;
    let dateB = date2;
    if(endOfDay){
        dateA = dateA.setHours(23, 59, 59, 999);
        dateB = dateB.setHours(23, 59, 59, 999);
    }
    const diff = parseInt((dateB - dateA) / (1000 * 60 * 60 * 24), 10);
    return isAbs ? Math.abs(diff) : diff;
}

export const getNDaysFrom = (date, days) => {
    return addDays(new Date(date), days);
}

export const getListofYears = () => {
    const currentYear = (new Date()).getFullYear();
    const range = (start, stop, step) => Array.from({ length: (stop - start) / step + 1}, (_, i) => start + (i * step));
    return range(currentYear, currentYear+10, 1);
}

export const getListOfMonths = (isFuture, year) => {
    const options = [
        {name: 'Jan', fullName: 'January', value: 1},
        {name: 'Feb', fullName: 'Feburary', value: 2},
        {name: 'Mar', fullName: 'March', value: 3},
        {name: 'Apr', fullName: 'April', value: 4},
        {name: 'May', fullName: 'May', value: 5},
        {name: 'Jun', fullName: 'June', value: 6},
        {name: 'Jul', fullName: 'July', value: 7},
        {name: 'Aug', fullName: 'August', value: 8},
        {name: 'Sep', fullName: 'September', value: 9},
        {name: 'Oct', fullName: 'October', value: 10},
        {name: 'Nov', fullName: 'November', value: 11},
        {name: 'Dec', fullName: 'December', value: 12},
    ];
    const currentYear = getCurrentYear();
    if (isFuture && (year == currentYear)) {
        const currentMonth = (new Date()).getMonth();
        return options.slice(currentMonth+1);
    }
    return options;
}

export const getCurrentYear = () => {
    return (new Date()).getFullYear();
}

export const getCurrentMonth = () => {
    return (new Date()).getMonth() + 1; // month starts from 0 in javascript
}

export const nth = (dt) => {
    const d = dt.getDate();
    if (d > 3 && d < 21) return 'th';
    switch (d % 10) {
    case 1:  return "st";
    case 2:  return "nd";
    case 3:  return "rd";
    default: return "th";
    }
}


export const getToday = () => {
    const today = new Date();
    const todayWithoutTime = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return todayWithoutTime;
}

export const getTomorrow = () => {
    const today = new Date();
    const tomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
    return tomorrow;
}

export const addNDays = (date, n) => {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + n);
    return newDate;
}

export const isFutureDate = (date) => {
    if (!date) return false;
    return new Date(date) > (new Date());
}

export const isPastDate = (date) => {
    if (!date) return false;
    return new Date(date) < (new Date());
}

export const toLocalDateString = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const parseLocalDate = (str) => {
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, m - 1, d);
};

export const isToday = (date, isDateTimeObject = false) => {
    if (!date) return false;
    const today = new Date();
    const dt = isDateTimeObject ? date : new Date(date);
    return dt.getDate() == today.getDate() &&
        dt.getMonth() == today.getMonth() &&
        dt.getFullYear() == today.getFullYear();
}

// NOTE: MUST PASS 2 DATES..
export const getLaterDate = (date, dateTwo) => {
    const dateObj = new Date(date);
    const dateTwoObj = new Date(dateTwo);
    return dateObj >= dateTwoObj ? date : dateTwo
}
